import React, { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  EntityExpiringDocumentsDto,
  ExpiringDocumentDto,
  ExpiringDocumentsParams,
  useGetExpiringDocumentsQuery,
} from "../../api/documentsApi";
import { DEFAULT_DATA_TABLE_PAGE_SIZE } from "../../constants/dataTable";
import PageTitle from "../../components/common/PageTitle";
import { SafetyBoardIcon } from "../../components/common/pageIcons";
import ExpiringDocumentsTable from "./components/ExpiringDocumentsTable";
import PageLayout from "../../components/layout/PageLayout";
import { PaginatedQueryParams } from "../../types/pagination";
import { useDataTablePagination } from "../../hooks/useDataTablePagination";

const EXPIRING_STATUS_VALUES = new Set([
  "expired",
  "critical",
  "warning",
  "valid",
]);

const buildExpiringDocumentsQueryArg = (
  queryParams: PaginatedQueryParams
): ExpiringDocumentsParams => {
  const filters = queryParams.filters ?? {};

  return {
    pageNumber: queryParams.pageNumber,
    pageSize: queryParams.pageSize,
    entityType: filters["entityType"] as string | undefined,
    documentCategory: filters["documentCategory"] as string | undefined,
    status: filters["daysUntilExpiration"] as string | undefined,
    search: filters["search"] as string | undefined,
  };
};

const SafetyPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const initialFilters = useMemo(() => {
    const status = searchParams.get("status");
    if (status && EXPIRING_STATUS_VALUES.has(status)) {
      return { daysUntilExpiration: status };
    }
    return undefined;
  }, [searchParams]);

  const {
    items: entityGroups,
    isLoading,
    isLoadingRows,
    isLoadingMore,
    totalCount,
    hasNextPage,
    handleSearchChange,
    handleLoadMore,
  } = useDataTablePagination<EntityExpiringDocumentsDto, ExpiringDocumentsParams>(
    useGetExpiringDocumentsQuery,
    DEFAULT_DATA_TABLE_PAGE_SIZE,
    {
      initialFilters,
      buildQueryArg: buildExpiringDocumentsQueryArg,
    }
  );
  const expiringDocuments = useMemo<ExpiringDocumentDto[]>(
    () => entityGroups.flatMap((group) => group.documents ?? []),
    [entityGroups]
  );

  return (
    <PageLayout>
      <PageTitle
        title="Safety Board"
        icon={<SafetyBoardIcon />}
        titleVariant="h4"
        subtitle="Monitor document expirations across vehicles, trailers, and permits"
      />

      <ExpiringDocumentsTable
        documents={expiringDocuments}
        isLoading={isLoading}
        totalCount={totalCount}
        hasNextPage={hasNextPage}
        isLoadingMore={isLoadingMore}
        isLoadingRows={isLoadingRows}
        onLoadMore={handleLoadMore}
        onSearchChange={handleSearchChange}
        initialTableFilters={initialFilters}
      />
    </PageLayout>
  );
};

export default SafetyPage;
